import { getAxiosInstance } from './axiosInstance';
import { useGlobalErrorHandler } from './useGlobalErrorHandler';
import { useAppContext } from '@context/AppContext';

export const useWFService = () => {
    const handleUnauthorized = useGlobalErrorHandler();
    const appState = useAppContext().state;

    const createTask = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.post('/tasks', params);
        return data;
    };

    const getTasks = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance(appState, handleUnauthorized, true);
        const { data } = await axiosInstance.post('/tasks/search', params);
        return data;
    };

    const getTaskDetail = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance(appState, handleUnauthorized, true);
        const { data } = await axiosInstance.get(`/tasks/${params.id}`);
        return data;
    };

    

    const getResumeStatus = async (params: any): Promise<any> => {
        let dateRange;
        if ( params.elaborationDate ) {
            dateRange = params.elaborationDate;
        } else {
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth();
            const day = now.getDate();
            // Usar Date.UTC para asegurar 00:00:00 y 23:59:59 en la fecha local
            const start = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
            const end = new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
            dateRange = { $gte: start, $lte: end };
        }
        const startDate = dateRange?.$gte ? dateRange.$gte.toISOString() : '';
        const endDate = dateRange?.$lte ? dateRange.$lte.toISOString() : '';
        const axiosInstance = getAxiosInstance(appState, handleUnauthorized, true);
        const { data } = await axiosInstance.get(
            `/tasks/count-by-status?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
            params
        );
        return data;
    };

    const getStatesByStage = async ( wf: string , stageId: string ): Promise<any> => {
        const axiosInstance = getAxiosInstance(appState, handleUnauthorized, true);
        const { data } = await axiosInstance.get(`/workflows/${wf}/${stageId}/statuses`);
        return data;
    };

    const getAllStages = async ( wf: string ): Promise<any> => {
        const axiosInstance = getAxiosInstance(appState, handleUnauthorized, true);
        const { data } = await axiosInstance.get(`/workflows/${wf}/stages`);
        return data;
    };

    const modifyTask = async ( taskId: string , params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        const { data } = await axiosInstance.put(`/tasks/${taskId}` , params);
        return data;
    };

    const taskNextStage = async ( taskId: string ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        const { data } = await axiosInstance.post(`/tasks/${taskId}/advance` );
        return data;
    };

    const taskPrevStage = async ( taskId: string , stage: string | null = null ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        const params = stage ? { stage } : {};
        const { data } = await axiosInstance.post( `/tasks/${taskId}/return` , params );
        return data;
    };

    const taskGoToState = async ( taskId: string , state: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        let payload: any;
        if (typeof state === 'string') {
            payload = { newStatus: state };
        } else {
            payload = { newStatus:  state.state , recreate: state.recreate };
        }
        const { data } = await axiosInstance.post( `/tasks/${taskId}/status` , payload );
        return data;
    };

    const calculateDays = async ( params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        const { data } = await axiosInstance.post( `/tasks/medications/calculateDays` , params );
        return data;
    };

    const getCampaings = async ( params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        const { data } = await axiosInstance.post(`/tasks/group?groupBy=campaingId` , params);
        return data;
    };

    const getGroupBy = async ( groupBy: string , params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true);
        const { data } = await axiosInstance.post(`/tasks/group?groupBy=${groupBy}` , params);
        return data;
    };

    const getPreviewPrintZPL = async ( params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true , true );
        const { data } = await axiosInstance.post(`/zpl/preview` , params);
        return data;
    };

    const getListLabelsPDF = async ( params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.get(`/labels`, {
            params: {
                institutionId: params.institutionId,
                from: params.from,
                to: params.to
            }
        });
        return data;
    };

    const postSendLabeled = async ( params: any ): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true , true );
        const { data } = await axiosInstance.post(`/labels` , params);
        return data;
    };

    const getLabelsPrint = async (): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.get(`/labels/print`);
        return data;
    };

    const postLabelsClaim = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.post(`/labels/print/claim` , params);
        return data;
    };

    const postLabelsSuccess = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.post(`/labels/print/${params}/ack` , {});
        return data;
    };

    const postLabelsFail = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.post(`/labels/print/${params}/fail` , {});
        return data;
    };

    const getLabelsPrintStatus = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.get(`/labels/print/automatic/status`);
        return data;
    };

    const postLabelsPermission = async (params: any): Promise<any> => {
        const axiosInstance = getAxiosInstance( appState , handleUnauthorized , true );
        const { data } = await axiosInstance.post(`/labels/print/automatic` , params);
        return data;
    };
    

    
    
    return {
        createTask ,
        getTasks ,
        getTaskDetail ,
        getResumeStatus ,
        getStatesByStage ,
        getAllStages ,
        modifyTask ,
        calculateDays ,
        taskNextStage ,
        taskPrevStage ,
        taskGoToState ,
        getCampaings ,
        getGroupBy ,
        getPreviewPrintZPL ,
        getListLabelsPDF ,
        postSendLabeled ,
        getLabelsPrint ,
        postLabelsClaim ,
        postLabelsSuccess ,
        postLabelsFail ,
        getLabelsPrintStatus , 
        postLabelsPermission
    };
};


